import { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { useQuestionnaireContext } from '@/components/Questionnaire/QuestionnaireProvider';
import * as QuestionnaireApiModels from '@typings/questionnaires-api-models';
import { useWatch } from 'react-hook-form';
import getFieldValue from '@/helpers/questionnaires/getFieldValue';

type VariableValues = Record<string, unknown>;
type Variable = Pick<QuestionnaireApiModels.ExpressionVariable,
  | 'referenceType'
  | 'referenceId'
  | 'selector'
  | 'variableName'
>;

const useVariableValues = (variables: Variable[] | null): VariableValues => {
  const {
    fieldsByKey,
    formMethods,
  } = useQuestionnaireContext();
  // @todo: handle other reference types, eg workflow context
  // @todo: handle other filterExpression stuff
  // @todo: handle variables in lists

  const variableValuesRef = useRef<VariableValues>({});
  const variablesToWatch = useMemo(() => (
    (variables || [])
      .filter((variable) => variable.referenceType === 'field-template-key')
  ), [variables]);

  const watchedFields = useWatch({
    control: formMethods.control,
    name: variablesToWatch.map((variable) => variable.referenceId),
  });

  useEffect(() => {
    const newVariableValues: VariableValues = {};
    variablesToWatch.forEach((variable, index) => {
      const variableField = fieldsByKey[variable.referenceId];
      // @todo - is this really necessary?
      // type=number should be numbers, so the `return Number(value)` should not be needed.
      // Only thing left is the "multi select field but only one field" and is that even the right thing to do?
      const fieldValue = getFieldValue({
        field: variableField,
        value: watchedFields[index],
      });
      newVariableValues[variable.variableName] = variable.selector
        ? _.get(fieldValue, variable.selector, null)
        : fieldValue;
    });

    if (!_.isEqual(newVariableValues, variableValuesRef.current)) {
      variableValuesRef.current = newVariableValues;
    }
  }, [variablesToWatch, watchedFields, fieldsByKey]);

  return variableValuesRef.current;
};

export default useVariableValues;